import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { Toaster } from "sonner";

// Pages
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
import DashboardPage from "./pages/DashboardPage";
import UnifiedDashboardPage from "./pages/UnifiedDashboardPage";
import TemplatesPage from "./pages/TemplatesPage";
import TemplateEditorPage from "./pages/TemplateEditorPage";
import SendPage from "./pages/SendPage";
import SendDocumentPage from "./pages/SendDocumentPage";
import BatchSendPage from "./pages/BatchSendPage";
import BulkSendPage from "./pages/BulkSendPage";
import SendPacketPage from "./pages/SendPacketPage";
import SendCredentialRequestPage from "./pages/SendCredentialRequestPage";
import DocumentsPage from "./pages/DocumentsPage";
import TrackingPage from "./pages/TrackingPage";
import RecipientsPage from "./pages/RecipientsPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
import SettingsPage from "./pages/SettingsPage";
import CandidateCompletionPage from "./pages/CandidateCompletionPage";
import CredentialRenewalPage from "./pages/CredentialRenewalPage";
import CredentialRequestPage from "./pages/CredentialRequestPage";
import CandidatePortalPage from "./pages/CandidatePortalPage";
import BullhornEmbedPage from "./pages/BullhornEmbedPage";
import BullhornCallbackPage from "./pages/BullhornCallbackPage";
import MasterAdminPage from "./pages/MasterAdminPage";
import AdminLoginPage from "./pages/AdminLoginPage";
import HomePage from "./pages/HomePage";
// PricingPage removed — pricing is handled off-site
import ContactSalesPage from "./pages/ContactSalesPage";
import PrivacyPage from "./pages/legal/PrivacyPage";
import CookiesPage from "./pages/legal/CookiesPage";
import DPAPage from "./pages/legal/DPAPage";
import SubProcessorsPage from "./pages/legal/SubProcessorsPage";
import CookieConsentBanner from "./components/CookieConsentBanner";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import FormsDashboardPage from "./pages/FormsDashboardPage";
import ShortLinkPage from "./pages/ShortLinkPage";
import FormsPage from "./pages/FormsPage";
import FormBuilderPage from "./pages/FormBuilderPage";
import FormSubmissionsPage from "./pages/FormSubmissionsPage";
import FormCompletionPage from "./pages/FormCompletionPage";
import PortalLoginPage from "./pages/PortalLoginPage";
import PortalResetPasswordPage from "./pages/PortalResetPasswordPage";

// Tenant-aware redirect component
function TenantRedirect() {
  const { isAuthenticated, tenantSlug, loading } = useAuth();
  const location = window.location.pathname;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated && tenantSlug) {
    // Preserve the current path segment under the tenant slug
    const pathSegment = location.split('/').filter(Boolean).pop() || 'dashboard';
    const validPaths = ['dashboard','templates','send','documents','settings','tracking','recipients','batch-send','bulk-send','analytics','email-templates','forms'];
    const targetPath = validPaths.includes(pathSegment) ? pathSegment : 'dashboard';
    return <Navigate to={`/${tenantSlug}/${targetPath}`} replace />;
  }
  
  return <Navigate to="/login" replace />;
}

// Public root: shows marketing homepage for unauthenticated visitors,
// redirects authenticated users to their tenant dashboard.
function RootRoute() {
  const { isAuthenticated, tenantSlug, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (isAuthenticated && tenantSlug) {
    return <Navigate to={`/${tenantSlug}/dashboard`} replace />;
  }
  
  return <HomePage />;
}

// Tenant slug validator component
function TenantRouteWrapper({ children }) {
  const { tenantSlug: urlSlug } = useParams();
  const { tenantSlug: authSlug, isAuthenticated, user } = useAuth();
  
  // Master admins can access any tenant workspace
  if (user?.is_master_admin) {
    return children;
  }
  
  // If URL tenant doesn't match authenticated user's tenant, redirect
  if (isAuthenticated && urlSlug && authSlug && urlSlug !== authSlug) {
    return <Navigate to={`/${authSlug}/dashboard`} replace />;
  }
  
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin" element={<AdminLoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
      
      {/* Public candidate completion route */}
      <Route path="/complete/:token" element={<CandidateCompletionPage />} />
      <Route path="/credential-renewal/:token" element={<CredentialRenewalPage />} />
      <Route path="/credential-request/:token" element={<CredentialRequestPage />} />

      {/* Public online-form completion route */}
      <Route path="/forms/complete/:token" element={<FormCompletionPage />} />
      
      {/* Public candidate portal route - view all documents */}
      <Route path="/portal/login" element={<PortalLoginPage />} />
      <Route path="/portal/login/:tenantSlug" element={<PortalLoginPage />} />
      <Route path="/portal/reset-password" element={<PortalResetPasswordPage />} />
      <Route path="/portal" element={<CandidatePortalPage />} />
      <Route path="/portal/:tenantSlug" element={<CandidatePortalPage />} />
      <Route path="/portal/access/:token" element={<CandidatePortalPage />} />

      {/* Short-link resolver — emails link here, we look up the long token and redirect. */}
      <Route path="/s/:code" element={<ShortLinkPage />} />
      
      {/* Master Admin route */}
      <Route path="/master" element={
        <ProtectedRoute>
          <MasterAdminPage />
        </ProtectedRoute>
      } />
      
      {/* Bullhorn embed route - for iframe integration */}
      <Route path="/embed/bullhorn" element={<BullhornEmbedPage />} />
      
      {/* Bullhorn OAuth callback */}
      <Route path="/auth/bullhorn/callback" element={<BullhornCallbackPage />} />
      
      {/* Tenant-scoped protected routes */}
      <Route path="/:tenantSlug/dashboard" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <UnifiedDashboardPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      <Route path="/:tenantSlug/dashboard/forms" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <FormsDashboardPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      <Route path="/:tenantSlug/dashboard/documents" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <DashboardPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/templates" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <TemplatesPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/templates/:templateId" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <TemplateEditorPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/send" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <SendPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/send/single" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <SendDocumentPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/send/batch" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <BatchSendPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/send/bulk" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <BulkSendPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/send/packet" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <SendPacketPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />

      <Route path="/:tenantSlug/send/credentials" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <SendCredentialRequestPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/documents" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <DocumentsPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/analytics" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <AnalyticsPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/email-templates" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <EmailTemplatesPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      <Route path="/:tenantSlug/settings" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <SettingsPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />

      {/* Online Forms (Jotform-style) — gated client-side by feature flag */}
      <Route path="/:tenantSlug/forms" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <FormsPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      <Route path="/:tenantSlug/forms/submissions" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <FormSubmissionsPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      <Route path="/:tenantSlug/forms/:formId" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <FormBuilderPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      <Route path="/:tenantSlug/forms/:formId/submissions" element={
        <ProtectedRoute>
          <TenantRouteWrapper>
            <FormSubmissionsPage />
          </TenantRouteWrapper>
        </ProtectedRoute>
      } />
      
      {/* Legacy routes - redirect to tenant-scoped routes */}
      <Route path="/dashboard" element={<TenantRedirect />} />
      <Route path="/templates" element={<TenantRedirect />} />
      <Route path="/send" element={<TenantRedirect />} />
      <Route path="/documents" element={<TenantRedirect />} />
      <Route path="/settings" element={<TenantRedirect />} />
      <Route path="/tracking" element={<TenantRedirect />} />
      <Route path="/recipients" element={<TenantRedirect />} />
      <Route path="/batch-send" element={<TenantRedirect />} />
      <Route path="/bulk-send" element={<TenantRedirect />} />
      <Route path="/analytics" element={<TenantRedirect />} />
      <Route path="/email-templates" element={<TenantRedirect />} />
      
      {/* Root - public homepage for unauth, dashboard redirect for authed */}
      <Route path="/" element={<RootRoute />} />
      {/* Pricing removed — redirect to home */}
      <Route path="/pricing" element={<Navigate to="/" replace />} />
      <Route path="/contact-sales" element={<ContactSalesPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/cookies" element={<CookiesPage />} />
      <Route path="/legal/dpa" element={<DPAPage />} />
      <Route path="/legal/subprocessors" element={<SubProcessorsPage />} />
      
      {/* Catch all - redirect to login or tenant dashboard */}
      <Route path="*" element={<TenantRedirect />} />
    </Routes>
  );
}

/**
 * Note: ``/:tenantSlug/dashboard`` now renders the single unified
 * ``UnifiedDashboardPage`` for both products. The previous
 * ``DashboardRouter`` (which picked between Documents and Forms based on
 * the last-used workspace) was removed so the dashboard becomes
 * overarching — always showing both products' KPIs in clearly-labelled
 * sections. The legacy single-workspace dashboards are still reachable
 * via ``/dashboard/documents`` and ``/dashboard/forms`` for deep links.
 */

const TOAST_OPTIONS = {
  style: { fontFamily: 'Fira Sans, sans-serif' },
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <CookieConsentBanner />
      </BrowserRouter>
      <Toaster 
        position="top-right" 
        toastOptions={TOAST_OPTIONS}
      />
    </AuthProvider>
  );
}

export default App;
